import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Prontuarios from '../../services/Prontuarios';
import { SweetAlert } from '../../components/sweetalert';
import { URL } from '../../config/url';
import { useAuth } from '../../context/auth';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import TableList from '../../components/TableComponents/TableList';
import TableHeadRowCenter from '../../components/TableComponents/TableHeadRowCenter';
import TableHeadRowStart from '../../components/TableComponents/TableHeadRowStart';
import TableBodyRowCenter from '../../components/TableComponents/TableBodyRowCenter';
import TableBodyRowStart from '../../components/TableComponents/TableBodyRowStart';
import { dataAtualFormatadaDDMMYYYY } from '../../utilities/dataAtualFormatadaDDMMYYYY';
import { newestFirst } from '../../utilities/latestProntuario';

// Aba "Prontuário" do cadastro do paciente: todos os prontuários dele, sem os filtros de data e de doutor da lista.
// A página monta a aba só quando ela é aberta (mountOnEnter), e a busca acontece nessa hora
const TabProntuarios = ({ pacienteId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreateProntuario = Boolean(user?.features?.includes('create:prontuario'));
  const canCreateContrato = Boolean(user?.features?.includes('create:contrato'));
  const [prontuarios, setProntuarios] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    Prontuarios.listAll(1, { paciente: pacienteId })
      .then((data) => {
        setProntuarios([...data.data.data].sort(newestFirst));
        setTotalRegistros(data.data.paging.total_records);
      })
      .catch((error) => {
        setFailed(true);
        SweetAlert.error(error?.response?.data?.message ?? error?.message, 'Prontuários');
      })
      .finally(() => setLoading(false));
  }, [pacienteId]);

  if (loading) return <div className='p-3'><LoadingSkeleton /></div>;

  // Sem a lista, a aba avisa em vez de mostrar "Sem registros"
  if (failed) return <small className='d-block p-3 text-danger'>Não foi possível carregar os prontuários. Recarregue a página.</small>;

  // O cadastro abre com o paciente preenchido e, depois de salvar, volta para esta aba
  const newProntuarioButton = canCreateProntuario ?
    <div className='d-flex justify-content-end px-3 pt-3'>
      <Button variant='success' className='col-12 col-md-auto' onClick={() => navigate(URL.prontuario_cadastro + '?paciente_id=' + pacienteId)}>+ Novo prontuário</Button>
    </div> : '';

  if (prontuarios.length === 0) {
    return (
      <>
        {newProntuarioButton}
        <div className='d-flex justify-content-center py-2'>
          <h4>Sem registros</h4>
        </div>
      </>
    );
  }

  return (
    <>
      {newProntuarioButton}

      <TableList>
        <thead>
          <tr>
            <TableHeadRowCenter text='Código' />
            <TableHeadRowCenter text='Progresso' />
            <TableHeadRowCenter text='Data' />
            <TableHeadRowStart text='Título' />
            <TableHeadRowStart text='Doutor' />
            <TableHeadRowCenter text='Ações' />
          </tr>
        </thead>

        <tbody>
          {prontuarios.map((prontuario) => (
            <tr key={prontuario.id} className='trBody'>
              <TableBodyRowCenter text={prontuario.codigo} menor={true} />
              <TableBodyRowCenter text={prontuario.progresso} menor={true} />
              <TableBodyRowCenter text={dataAtualFormatadaDDMMYYYY(prontuario.data_cadastro)} menor={true} />
              <TableBodyRowStart text={prontuario.titulo} menor={true} />
              <TableBodyRowStart text={prontuario.tecnico_responsavel?.nome} menor={true} />

              <td className='tableRowCenter'>
                <div className='buttonContainer'>
                  <Button type='button' variant='outline-secondary' className='m-0 p-0 px-2' onClick={() => navigate(URL.prontuario_cadastro + '/' + prontuario.id)}>Editar</Button>
                  {/* Só para o prontuário sem nenhum contrato: o contrato abre com o paciente e o prontuário preenchidos */}
                  {(canCreateContrato && prontuario.has_contract === false) ?
                    <Button type='button' variant='outline-success' className='m-0 p-0 px-2 text-nowrap' onClick={() => navigate(URL.contrato_cadastro + '?prontuario_id=' + prontuario.id)}>Criar contrato</Button> : ''}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </TableList>

      <div className='px-3 pb-3'>
        <span>Total: {totalRegistros}</span>
      </div>
    </>
  );
};

export default TabProntuarios;
